import { Injectable } from '@angular/core';
import * as moment from 'moment';
import { BehaviorSubject, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { PolicyBase, PolicyBaseType } from '../../models/policy';
import { createObjectOfPoliciesById } from '../../utils/utils';
import { MMobileHelperService } from '../core/mmobile-helper.service';

interface PoliciesState {
  [key: string]: any;
  policiesByBaseType: PoliciesByBaseType;
}

export type PoliciesByBaseType = { [key in PolicyBaseType]: PolicyBase[] };
@Injectable({
  providedIn: 'root'
})
export class PoliciesContextService {
  analyticsPolicies = {};

  private initialState: PoliciesState = { policiesByBaseType: {} as any };
  private policies$: BehaviorSubject<PoliciesState> = new BehaviorSubject(
    this.initialState
  );
  private stateLastUpdate = moment();

  private policiesReloadNeeded$ = new Subject<any>();
  private policiesBackNavigationParamsSubject = new Subject<any>();
  private policyAuthorizationsReloadNeeded$ = new BehaviorSubject(false);
  private changeAuthorizationsSection$ = new BehaviorSubject(false);
  private changeAppointmentsSection$ = new BehaviorSubject(false);

  constructor(private mmobileHelper: MMobileHelperService) {}

  /**
   * @important Hotfix, this method should only be called by policies-helper service
   */
  getPolicies() {
    if (this.isStateInvalid()) {
      this.clear();
    }
    return this.policies$.getValue();
  }

  /**
   * @important Hotfix, this method should only be called by policies-helper service
   */
  getPoliciesByBaseType(policyBaseType: PolicyBaseType) {
    if (this.isStateInvalid()) {
      this.clear();
    }
    return this.policies$.getValue().policiesByBaseType[policyBaseType];
  }

  getPoliciesByBaseTypeObs() {
    if (this.isStateInvalid()) {
      this.clear();
    }
    return this.policies$.pipe(
      map(({ policiesByBaseType }) => policiesByBaseType)
    );
  }

  getPolicy(policyNumber: string) {
    if (this.isStateInvalid()) {
      this.clear();
    }
    return this.policies$.getValue()[policyNumber] as unknown;
  }

  getAnalytics() {
    return this.analyticsPolicies;
  }

  clear() {
    this.analyticsPolicies = {};
    this.policies$.next(this.initialState);
  }

  addPolicies(policyBaseType: PolicyBaseType, policies: PolicyBase[]) {
    this.stateLastUpdate = moment();
    const policiesById = createObjectOfPoliciesById(policies);

    this.policies$.next({
      ...this.policies$.getValue(),
      ...policiesById,
      policiesByBaseType: {
        ...this.policies$.getValue().policiesByBaseType,
        [policyBaseType]: policies
      }
    });
    this.calculateAnalytics();
  }

  getPolicyAuthorizationsReloadNeededEventObservable() {
    return this.policyAuthorizationsReloadNeeded$.asObservable();
  }

  emitPolicyAuthorizatiosnReloadNeeded(value: boolean) {
    this.policyAuthorizationsReloadNeeded$.next(value);
  }

  getChangeToAppointmentsSectionEventObservable() {
    return this.changeAppointmentsSection$.asObservable();
  }

  emitChangeToAppointmentsSection(value: boolean) {
    this.changeAppointmentsSection$.next(value);
  }

  getChangeToAuthorizationsSectionEventObservable() {
    return this.changeAuthorizationsSection$.asObservable();
  }

  emitChangeToAuthorizationsSection(value: boolean) {
    this.changeAuthorizationsSection$.next(value);
  }

  getPoliciesReloadNeededEventObservable() {
    return this.policiesReloadNeeded$.asObservable();
  }

  emitPoliciesReloadNeededEvent(params?) {
    this.clear();
    this.policiesReloadNeeded$.next(params);
  }

  getPoliciesBackNavigationParamsObservable() {
    return this.policiesBackNavigationParamsSubject.asObservable();
  }

  emitBackNavigationParams(params: any) {
    this.policiesBackNavigationParamsSubject.next(params);
  }

  private calculateAnalytics() {
    const policiesArr = Object.values(this.getPolicies());
    if (policiesArr && policiesArr.length) {
      this.analyticsPolicies = {
        CD65_aci_client_products: policiesArr
          .map(({ policyBaseType }) => policyBaseType)
          .join(';'),
        CD66_aci_client_modalities: policiesArr
          .map(({ product }) => product)
          .join(';'),
        CD67_aci_service_product: policiesArr
          .map(
            ({ policyBaseType, product }) =>
              (policyBaseType || '') + ' - ' + (product || '')
          )
          .join(';'),
        CD69_aci_number_products: policiesArr.length,
        CD70_aci_client_expiration_date: policiesArr
          .map(p => p.expirationDate || '')
          .join(';'),
        CD81_aci_unique_client_products: [
          ...new Set(policiesArr.map(({ policyBaseType }) => policyBaseType))
        ].join(';')
      };
    }
  }

  private isStateInvalid() {
    const minsToInvalidateState =
      this.mmobileHelper.getCustomConfigData(
        'STATE',
        'INVALIDATE_POLICIES_LIST_MINS'
      ) || 0; // Default to non cached
    const minsSinceLastUpdateToState = moment().diff(
      this.stateLastUpdate,
      'minutes'
    );
    return minsSinceLastUpdateToState >= minsToInvalidateState;
  }
}
